import * as Collapsible from "@radix-ui/react-collapsible";
import * as Tabs from "@radix-ui/react-tabs";
import { DOMParser } from "@xmldom/xmldom";
import { MinusIcon, PlusIcon } from "lucide-react";
import { Fragment, createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useHighlighter, useSchemaViewContext } from "zudoku";
import { Button } from "zudoku/ui/Button.js";
import { Item, ItemActions, ItemContent, ItemGroup, ItemTitle } from "zudoku/ui/Item.js";
import { SyntaxHighlight } from "zudoku/ui/SyntaxHighlight.js";
import { cn } from "zudoku/ui/util.js";

// TextMate grammar for Relax NG Compact Notation, sourced from redhat-developer/vscode-xml.
const rncGrammar = {
  name: "rnc",
  displayName: "Relax NG Compact",
  scopeName: "source.rnc",
  fileTypes: ["rnc"],
  foldingStartMarker: "\\{\\s*$",
  foldingStopMarker: "^\\s*\\}",
  patterns: [
    { match: "\\bgrammar\\b", name: "keyword.other.grammar.rnc" },
    { match: "\\bstart\\b", name: "keyword.other.start.rnc" },
    {
      match: "\\b(text|empty|xsd\\:(int|integer|double|date(Time)?|time|string|decimal))\\b",
      name: "storage.type.rnc",
    },
    {
      captures: { 1: { name: "keyword.other.attribute.rnc" }, 2: { name: "entity.other.attribute.rnc" } },
      match: "\\b(attribute)\\s+([a-zA-Z][-a-zA-Z_0-9]*)\\s*\\{",
      name: "meta.declaration.attribute.rnc",
    },
    {
      captures: { 1: { name: "keyword.other.element.rnc" }, 2: { name: "entity.other.element.rnc" } },
      match: "\\b(element)\\s+([a-zA-Z][-a-zA-Z_0-9]*)\\s*\\{",
      name: "meta.declaration.element.rnc",
    },
    { match: "#.*$", name: "comment.hash.rnc" },
    {
      begin: "\"",
      end: "\"",
      name: "string.quoted.double.rnc",
      patterns: [{ match: "\\\\.", name: "constant.character.escape.rnc" }],
    },
    {
      begin: "'",
      end: "'",
      name: "string.quoted.single.rnc",
      patterns: [{ match: "\\\\.", name: "constant.character.escape.rnc" }],
    },
    {
      captures: { 1: { name: "entity.other.grammar_production.rnc" } },
      match: "\\b([a-zA-Z][a-zA-Z_0-9]*)\\s*=",
      name: "meta.grammar_production.rnc",
    },
  ],
};

// Href builder for schema type links, provided by RngPanel.
const SchemaHrefContext = createContext<(typeName: string) => string>(() => "#");

// Loaded on first RncTab mount; shared across all instances.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let rncLoaded: Promise<void> | null = null;

// --- RNG → RNC converter --------------------------------------------------

const NS_A = "http://relaxng.org/ns/compatibility/annotations/1.0";

function childEls(el: Element): Element[] {
  return Array.from(el.childNodes).filter(
    (n): n is Element => n.nodeType === 1, // Node.ELEMENT_NODE
  );
}

function getDoc(el: Element): string | null {
  for (const c of childEls(el))
    if (c.namespaceURI === NS_A && c.localName === "documentation")
      return c.textContent?.trim() ?? null;
  return null;
}

// Strip annotation children from an element's child list.
function rngKids(el: Element): Element[] {
  return childEls(el).filter(
    (c) => !(c.namespaceURI === NS_A && c.localName === "documentation"),
  );
}

function ind(depth: number): string {
  return "  ".repeat(depth);
}

// Render a node; depth is the current indentation level.
function rncNode(el: Element, depth: number): string {
  const kids = rngKids(el);

  switch (el.localName) {
    case "element": {
      const name = el.getAttribute("name")!;
      const doc  = getDoc(el);
      const body = rncBody(kids, depth + 1);
      const trimmed = body.trim();
      if (!doc && !trimmed.includes("\n") && trimmed.length <= 40)
        return `element ${name} { ${trimmed} }`;
      const comment = doc ? `${ind(depth + 1)}## ${doc}\n` : "";
      return `element ${name} {\n${comment}${body}\n${ind(depth)}}`;
    }
    case "attribute":
      return `attribute ${el.getAttribute("name")!} { text }`;
    case "text":
    case "empty":
      return el.localName;
    case "optional":
      return rncSuffix(kids, depth, "?");
    case "zeroOrMore":
      return rncSuffix(kids, depth, "*");
    case "oneOrMore":
      return rncSuffix(kids, depth, "+");
    case "choice":
      return rncList(kids, depth, " | ", "| ");
    case "interleave":
      return rncList(kids, depth, " & ", "& ");
    case "group":
      return rncList(kids, depth, ", ", ", ");
    case "ref":
      return el.getAttribute("name")!;
    case "data": {
      const lib = el.ownerDocument!.documentElement.getAttribute("datatypeLibrary") ?? "";
      const prefix = lib.split("/").filter(Boolean).at(-1);
      const type = el.getAttribute("type") ?? "string";
      return prefix ? `${prefix}:${type}` : type;
    }
    default:
      return "";
  }
}

// Render the interior of an element or define at the given depth.
function rncBody(kids: Element[], depth: number): string {
  if (kids.length === 0) return `${ind(depth)}empty`;
  if (kids.length === 1) return rncIndent(rncNode(kids[0], depth), depth);
  // multiple children in a define body → sequence
  return kids
    .map((k) => rncIndent(rncNode(k, depth), depth))
    .join(",\n");
}

// Indent every line of s by depth.
function rncIndent(s: string, depth: number): string {
  return s
    .split("\n")
    .map((l) => `${ind(depth)}${l}`)
    .join("\n");
}

// Apply a cardinality suffix (?/*/ +), wrapping in () when needed.
function rncSuffix(kids: Element[], depth: number, suffix: string): string {
  if (kids.length === 1) {
    const s = rncNode(kids[0], depth);
    const needsParens =
      kids[0].localName === "choice" ||
      kids[0].localName === "interleave" ||
      kids[0].localName === "group";
    if (needsParens) return `(${s})${suffix}`;
    return `${s}${suffix}`;
  }
  const inner = kids.map((k) => rncIndent(rncNode(k, depth + 1), depth + 1)).join(",\n");
  return `(\n${inner}\n${ind(depth)})${suffix}`;
}

// Join children with a separator, going multi-line when needed.
function rncList(
  kids: Element[],
  depth: number,
  inlineSep: string,
  multiPrefix: string,
): string {
  const parts = kids.map((k) => rncNode(k, depth));
  const inline = parts.join(inlineSep);
  if (!inline.includes("\n") && inline.length <= 60) return inline;
  return parts
    .map((p, i) => (i === 0 ? p : `${ind(depth)}${multiPrefix}${p}`))
    .join("\n");
}

function toRnc(xml: string): string {
  const doc = new DOMParser().parseFromString(xml, "text/xml");

  const defines = new Map<string, Element>();
  for (const def of Array.from(doc.getElementsByTagName("define"))) {
    const name = def.getAttribute("name");
    if (name) defines.set(name, def);
  }

  // Inline refs so rncNode sees real elements, not stubs.
  // Simple approach: replace each <ref> in the DOM with the define's children.
  function inlineRefs(root: Element) {
    for (const ref of Array.from(root.getElementsByTagName("ref"))) {
      const def = defines.get(ref.getAttribute("name")!);
      if (!def) continue;
      const kids = childEls(def);
      if (kids.length === 1) {
        ref.parentNode!.replaceChild(kids[0].cloneNode(true) as Element, ref);
      }
    }
  }

  const root = doc.documentElement;
  inlineRefs(root);

  const lines: string[] = [];

  const lib = root.getAttribute("datatypeLibrary") ?? "";
  const prefix = lib.split("/").filter(Boolean).at(-1);
  if (prefix) lines.push(`datatypes ${prefix} = "${lib}"`);

  // RELAX NG allows either a <grammar> with <start>, or a bare pattern as the root.
  let startPattern: Element | null = null;
  if (root.localName === "grammar") {
    const start = childEls(root).find((c) => c.localName === "start") ?? null;
    const startKids = start ? childEls(start) : [];
    if (startKids.length === 1) startPattern = startKids[0];
  } else {
    startPattern = root;
  }
  if (!startPattern) return "";

  lines.push(`start = ${rncNode(startPattern, 0)}`);

  return lines.join("\n\n");
}

// --- RNG parser (for structure tab) ---------------------------------------

type Cardinality = "required" | "optional" | "zero-or-more" | "one-or-more";

type RngNode =
  | { kind: "element"; name: string; cardinality: Cardinality; contentType: string; children: RngNode[]; doc?: string }
  | { kind: "attribute"; name: string; cardinality: Cardinality }
  | { kind: "choice"; options: RngNode[][]; cardinality: Cardinality };

function parseNodes(
  els: Element[],
  defines: Map<string, Element>,
  cardinality: Cardinality,
): RngNode[] {
  return els.flatMap((el) => parseNode(el, defines, cardinality));
}

function parseNode(
  el: Element,
  defines: Map<string, Element>,
  cardinality: Cardinality = "required",
): RngNode[] {
  switch (el.localName) {
    case "element": {
      const name = el.getAttribute("name")!;
      const doc  = getDoc(el);
      const allKids = rngKids(el);
      const hasText = allKids.some((c) => c.localName === "text");
      const dataChild = allKids.find((c) => c.localName === "data");
      const children = parseNodes(
        allKids.filter((c) => c.localName !== "text" && c.localName !== "data"),
        defines,
        "required",
      );
      let contentType: string;
      if (children.length > 0) contentType = "complex";
      else if (hasText) contentType = "text";
      else if (dataChild) {
        const lib = dataChild.ownerDocument!.documentElement.getAttribute("datatypeLibrary") ?? "";
        const pfx = lib.split("/").filter(Boolean).at(-1);
        const type = dataChild.getAttribute("type") ?? "string";
        contentType = pfx ? `${pfx}:${type}` : type;
      } else contentType = "empty";
      return [{ kind: "element", name, cardinality, contentType, children, ...(doc ? { doc } : {}) }];
    }
    case "attribute":
      return [{ kind: "attribute", name: el.getAttribute("name")!, cardinality }];
    case "text":
      return [];
    case "optional":
      return parseNodes(childEls(el), defines, "optional");
    case "zeroOrMore":
      return parseNodes(childEls(el), defines, "zero-or-more");
    case "oneOrMore":
      return parseNodes(childEls(el), defines, "one-or-more");
    case "interleave":
    case "group":
      return parseNodes(childEls(el), defines, cardinality);
    case "choice": {
      const options = childEls(el).map((c) => parseNode(c, defines, cardinality));
      const flat = options.flat();
      if (flat.length <= 1) return flat;
      return [{ kind: "choice", options: options.filter((o) => o.length > 0), cardinality }];
    }
    case "ref": {
      const def = defines.get(el.getAttribute("name")!);
      if (!def) return [];
      return parseNodes(childEls(def), defines, cardinality);
    }
    default:
      return [];
  }
}

function parseRng(xml: string): RngNode[] {
  const doc = new DOMParser().parseFromString(xml, "text/xml");
  const defines = new Map<string, Element>();
  for (const def of Array.from(doc.getElementsByTagName("define"))) {
    const name = def.getAttribute("name");
    if (name) defines.set(name, def);
  }
  const root = doc.documentElement;
  // RELAX NG allows either a <grammar> with <start>, or a bare pattern as the root.
  const els = root.localName === "grammar"
    ? childEls(doc.getElementsByTagName("start")[0] ?? root)
    : [root];
  return parseNodes(els, defines, "required");
}

// --- Structure visualization ----------------------------------------------

const CARDINALITY_LABEL: Record<Cardinality, string | null> = {
  required:       null,
  optional:       "optional",
  "zero-or-more": "*",
  "one-or-more":  "+",
};

function NodeRow({ node }: { node: RngNode }) {
  const [isOpen, setIsOpen] = useState(true);
  const schemaHref = useContext(SchemaHrefContext);

  if (node.kind === "choice") {
    return (
      <Item size="sm">
        <ItemContent>
          <div className="text-xs text-muted-foreground italic">one of:</div>
          <div className="flex flex-col gap-2">
            {node.options.map((option, i) => (
              <div key={i} className="rounded-md border overflow-hidden">
                {option.map((child, j) => (
                  <Fragment key={j}>
                    {j > 0 && <div className="h-px bg-border" />}
                    <NodeRow node={child} />
                  </Fragment>
                ))}
              </div>
            ))}
          </div>
        </ItemContent>
      </Item>
    );
  }

  const isAttr     = node.kind === "attribute";
  const hasChildren = !isAttr && node.kind === "element" && node.children.length > 0;
  const label      = isAttr ? `@${node.name}` : node.name;
  const cardLabel  = CARDINALITY_LABEL[node.cardinality];
  const typeLabel  = isAttr
    ? "text"
    : node.contentType === "complex" ? null : node.contentType;

  return (
    <Item size="sm">
      <ItemContent>
        {node.kind === "element" && node.doc && (
          <div className="text-xs text-muted-foreground/70 italic">{node.doc}</div>
        )}
        <div>
          <ItemTitle className="inline me-2">
            <code>{label}</code>
          </ItemTitle>
          {isAttr ? (
            <span className="text-xs text-amber-600 dark:text-amber-400">attribute</span>
          ) : (
            <span className="text-xs text-sky-600 dark:text-sky-400">element</span>
          )}
          {typeLabel && (
            <>
              <span className="text-muted-foreground/50">&nbsp;&middot;&nbsp;</span>
              {typeLabel.includes(":") ? (
                <a href={schemaHref(typeLabel.split(":")[1])} className="text-xs text-muted-foreground hover:underline">
                  {typeLabel}
                </a>
              ) : (
                <span className="text-xs text-muted-foreground">{typeLabel}</span>
              )}
            </>
          )}
          {cardLabel && (
            <>
              <span className="text-muted-foreground/50">&nbsp;&middot;&nbsp;</span>
              <span className="text-xs text-muted-foreground">{cardLabel}</span>
            </>
          )}
        </div>
      </ItemContent>

      {hasChildren && (
        <ItemActions className="self-start">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? <MinusIcon size={16} /> : <PlusIcon size={16} />}
          </Button>
        </ItemActions>
      )}

      {hasChildren && (
        <Collapsible.Root
          open={isOpen}
          onOpenChange={setIsOpen}
          className={cn("w-full", !isOpen && "contents")}
        >
          <Collapsible.Content asChild>
            <ItemContent>
              <ItemGroup className="overflow-clip rounded-md border">
                {node.children.map((child, i) => (
                  <NodeRow key={i} node={child} />
                ))}
              </ItemGroup>
            </ItemContent>
          </Collapsible.Content>
        </Collapsible.Root>
      )}
    </Item>
  );
}

function RngStructure({ rng }: { rng: string }) {
  const nodes = parseRng(rng);
  if (nodes.length === 0) {
    return <div className="text-xs text-muted-foreground p-4">Could not parse schema.</div>;
  }
  return (
    <ItemGroup className="overflow-clip">
      {nodes.map((node, i) => <NodeRow key={i} node={node} />)}
    </ItemGroup>
  );
}

// --- RNC tab with deferred language load ----------------------------------

function RncTab({ code }: { code: string }) {
  const [html, setHtml] = useState<string | null>(null);
  const highlighter = useHighlighter();
  const schemaHref = useContext(SchemaHrefContext);

  useEffect(() => {
    if (!rncLoaded) rncLoaded = highlighter.loadLanguage(rncGrammar as any);
    rncLoaded.then(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const raw = (highlighter as any).codeToHtml(code, {
        lang: "rnc",
        themes: { light: "github-light", dark: "github-dark" },
        defaultColor: false,
      }) as string;

      const prefix = code.match(/^datatypes\s+(\w+)\s*=/m)?.[1];
      let processed = raw;
      if (prefix) {
        // Wrap type references in text nodes only (between > and <).
        processed = raw.replace(/>([^<]+)</g, (_, text: string) => {
          const linked = text.replace(
            new RegExp(`\\b${prefix}:([\\w-]+)`, "g"),
            (_: string, typeName: string) =>
              `<a href="${schemaHref(typeName)}" style="text-decoration:underline;cursor:pointer">${prefix}:${typeName}</a>`,
          );
          return `>${linked}<`;
        });
      }
      setHtml(processed);
    });
  }, [highlighter, code, schemaHref]);

  if (!html) return null;
  return (
    <div
      className="rounded-none text-xs max-h-[400px] overflow-auto [&_pre]:p-4 [&_pre]:m-0 [&_pre]:text-xs [&_pre]:font-mono"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// --- Plugin component -----------------------------------------------------

const triggerClass = cn(
  "px-3 py-2 text-xs text-muted-foreground -mb-px",
  "data-[state=active]:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-primary",
);

export function RngPanel({ rng, className }: { rng: string; className?: string }) {
  const [tab, setTab] = useState("rnc");
  const rnc = toRnc(rng);
  const { pathname } = useLocation();
  const apiBase = pathname.split("/").slice(0, 3).join("/");
  // zudoku slugifies schema headings to lowercase, so the anchor for e.g.
  // "dateTime" is "#datetime"; lowercase the fragment to keep links working.
  const schemaHref = (typeName: string) => `${apiBase}/~schemas#${typeName.toLowerCase()}`;
  return (
    <SchemaHrefContext.Provider value={schemaHref}>
    <Tabs.Root value={tab} onValueChange={setTab} className={className}>
      <Tabs.List className="flex border-b">
        <Tabs.Trigger value="rnc"       className={triggerClass}>Compact</Tabs.Trigger>
        <Tabs.Trigger value="structure" className={triggerClass}>Structure</Tabs.Trigger>
        <Tabs.Trigger value="xml"       className={triggerClass}>Relax NG</Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content value="rnc">
        <RncTab code={rnc} />
      </Tabs.Content>
      <Tabs.Content value="structure">
        <RngStructure rng={rng} />
      </Tabs.Content>
      <Tabs.Content value="xml">
        <SyntaxHighlight
          embedded
          language="xml"
          className="rounded-none text-xs max-h-[400px] overflow-auto"
          code={rng}
        />
      </Tabs.Content>
    </Tabs.Root>
    </SchemaHrefContext.Provider>
  );
}

// --- Slot component -------------------------------------------------------

export function RngSchemaBody() {
  const schema = useSchemaViewContext();
  const rng = schema?.["x-rng-schema"] as string | undefined;
  if (!rng) return null;
  return <RngPanel rng={rng.trim()} className="border-t" />;
}
